import { Component, input, output, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { Column, SetupWizardDynamicTable } from '../../../setup-wizard-dynamic-table/setup-wizard-dynamic-table';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { CommonModule } from '@angular/common';
import { BrowserService } from '../../../../../../../services/browser.service';
import { HeaderColumn } from '../../../setup-wizard-dynamic-table/header-column';
import { RowColumn } from '../../../setup-wizard-dynamic-table/row-column';
import {
	BeforeAfter,
	LOCATION_CSV_LABELS,
	SetupWizardBooleanType,
	SetupWizardLocation,
	LocationCsvKeys,
	LocationCsvTemplate,
	EMPTY_LOCATION_CSV_TEMPLATE,
	SetupWizardPayload,
	SetupWizardUser,
	TextUtils,
} from '@mira-avant/mtlibs';
import { SetupWizardBeforeAfter } from '../../setup-wizard-before-after/setup-wizard-before-after';
import { makeStatusColumn } from '../../setup-wizard-before-after-status/setup-wizard-before-after-status';
import { SetupWizard } from '../../../../../../../services/setup-wizard';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { MAX_FILTER_OPTIONS_ALLOWED } from '../../setup-wizard-edit-steps';
import { SetupWizardLocationUsersStatus } from './setup-wizard-location-users-status/setup-wizard-location-users-status';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { SetupWizardBeforeAfterStatusWithAssignment } from '../../setup-wizard-before-after-status-with-assignment/setup-wizard-before-after-status-with-assignment';

//Column to house location details for each location
export function makeUsersColumn<T>(): Column {
	return {
		key: 'users',
		name: 'Users',
	};
}

export function makeAssignedColumn<T>(table?: SetupWizardLocationsTable): Column {
	return {
		key: 'assigned',
		name: 'Assigned',
		sortOrder: null,
		// For sorting prefer the after.active property if it exists (is true or false)
		// and fall back to before.active if it is not present
		sortFn: (a: SetupWizardLocation, b: SetupWizardLocation) =>
			(a.after?.active ?? a.before?.active)
				?.toString()
				.localeCompare((b.after?.active ?? b.before?.active)?.toString()!) ?? -1,
		sortDirections: ['ascend', 'descend', null],
		filterMultiple: true,
		filterOptions: [...new Set([SetupWizardBooleanType.TRUE, SetupWizardBooleanType.FALSE])].map(value => ({
			text: value,
			value: value,
		})),
		filterFn: (list: string[], item: SetupWizardLocation) => {
			if (!table) {
				return false;
			}

			const isAssigned: boolean = table.isSelectedUserAssigned(item);

			//List will contain a string of 'true' or 'false' or both
			return list.includes(isAssigned.toString());
		},
	};
}

export function makeHistoricalColumn<T>(table?: SetupWizardLocationsTable): Column {
	return {
		key: 'historical',
		name: 'Historical',
		sortOrder: null,
		// For sorting prefer the after.active property if it exists (is true or false)
		// and fall back to before.active if it is not present
		sortFn: (a: SetupWizardLocation, b: SetupWizardLocation) =>
			(a.after?.active ?? a.before?.active)
				?.toString()
				.localeCompare((b.after?.active ?? b.before?.active)?.toString()!) ?? -1,
		sortDirections: ['ascend', 'descend', null],
		filterMultiple: true,
		filterOptions: [...new Set([SetupWizardBooleanType.TRUE, SetupWizardBooleanType.FALSE])].map(value => ({
			text: value,
			value: value,
		})),
		filterFn: (list: string[], item: SetupWizardLocation) => {
			if (!table) {
				return false;
			}

			const isHistorical: boolean = table.isSelectedUserHistorical(item);

			//List will contain a string of 'true' or 'false' or both
			return list.includes(isHistorical.toString());
		},
	};
}

const FULL_WIDTH_BREAKPOINT = 1510;

@Component({
	selector: 'app-setup-wizard-locations-table',
	imports: [
		CommonModule,
		SetupWizardDynamicTable,
		NzIconModule,
		RowColumn,
		HeaderColumn,
		SetupWizardBeforeAfter,
		NzTooltipModule,
		NzSpinModule,
		SetupWizardLocationUsersStatus,
		NzButtonModule,
		NzPopconfirmModule,
		SetupWizardBeforeAfterStatusWithAssignment,
	],
	standalone: true,
	templateUrl: './setup-wizard-locations-table.html',
	styleUrl: './setup-wizard-locations-table.scss',
})
export class SetupWizardLocationsTable {
	private clientUuid: string | undefined;
	private sub?: Subscription;
	private resizeSub?: Subscription;
	public loading = signal<boolean>(true);
	public pageSize = input<number>(10);
	public rows = signal<SetupWizardLocation[]>([]);
	readonly rowType = null! as BeforeAfter<LocationCsvTemplate>; // type inference only
	public columns = signal<Column[]>([]);
	private wideColumns: Column[] = [];
	private narrowColumns: Column[] = [];
	public onEdit = output<SetupWizardLocation>();
	public onEditUsers = output<SetupWizardLocation>();
	public onToggleDisable = output<SetupWizardLocation>();
	public onRemove = output<SetupWizardLocation>();
	public onReset = output<SetupWizardLocation>();
	public onAssign = output<{ user: SetupWizardUser; location: SetupWizardLocation }>();
	public onUnassign = output<{ user: SetupWizardUser; location: SetupWizardLocation }>();
	public enableEditButton = input<boolean>(true);
	public enableToggleDisableButton = input<boolean>(true);
	public enableRemoveButton = input<boolean>(true);
	public enableResetButton = input<boolean>(true);
	public enableAssignButton = input<boolean>(false);
	public enableUnassignButton = input<boolean>(false);
	public selectedRow = input<BeforeAfter<LocationCsvTemplate> | null>(null);
	public selectedUser = input<SetupWizardUser | null>(null);
	public columnsFilter = input<string[]>([]);
	public LocationCsvKeys = LocationCsvKeys;
	public LOCATION_CSV_LABELS = LOCATION_CSV_LABELS;
	public SetupWizardBooleanType = SetupWizardBooleanType;

	private ssoLocationIdFilterOptions: { text: string; value: string }[] = [];
	private nameFilterOptions: { text: string; value: string }[] = [];
	private displayOrderFilterOptions: { text: string; value: number }[] = [];

	private ssoLocationIdColumn: Column | undefined;
	private nameColumn: Column | undefined;
	private displayOrderColumn: Column | undefined;
	private activeColumn: Column | undefined;

	constructor(
		private route: ActivatedRoute,
		private browserService: BrowserService,
		private setupWizard: SetupWizard
	) {
		this.sub = this.route.params.subscribe(params => {
			this.clientUuid = params['clientUuid'];
		});
	}

	async ngOnInit() {
		await this.initTable();

		this.sub?.add(
			this.setupWizard.preview$.subscribe(payload => {
				this.loading.set(true);

				if (payload) {
					this.updateRows(payload);
				}

				this.loading.set(false);
			})
		);
	}

	public isSelectedUserAssigned(location: SetupWizardLocation): boolean {
		const locationBeforeAfter = location.after ?? location.before;

		if (!locationBeforeAfter) {
			return false;
		}

		//Test identifying if the user is assigned in a particular location
		const selectedUser = this.selectedUser();

		if (!selectedUser) {
			return false;
		}

		const userBeforeAfter = selectedUser.after ?? selectedUser.before;

		if (!userBeforeAfter) {
			return false;
		}

		return this.setupWizard.preview$.value.payload.schoolAdminsToLocations.some((userToLocation, index) => {
			const linkBeforeAfter = userToLocation.after ?? userToLocation.before;

			if (!linkBeforeAfter) {
				return false;
			}

			const doesLinkMatchLocation =
				(TextUtils.isNonEmptyString(linkBeforeAfter.sso_location_id) &&
					TextUtils.isNonEmptyString(locationBeforeAfter.sso_location_id) &&
					linkBeforeAfter.sso_location_id === locationBeforeAfter.sso_location_id) ||
				(TextUtils.isNonEmptyString(linkBeforeAfter.location_uuid) &&
					TextUtils.isNonEmptyString(locationBeforeAfter.location_uuid) &&
					linkBeforeAfter.location_uuid === locationBeforeAfter.location_uuid);

			if (!doesLinkMatchLocation) {
				return false;
			}

			const doesLinkMatchUser =
				(TextUtils.isNonEmptyString(linkBeforeAfter.school_id_or_email) &&
					TextUtils.isNonEmptyString(userBeforeAfter.school_id_or_email) &&
					linkBeforeAfter.school_id_or_email === userBeforeAfter.school_id_or_email) ||
				(TextUtils.isNonEmptyString(linkBeforeAfter.user_uuid) &&
					TextUtils.isNonEmptyString(userBeforeAfter.user_uuid) &&
					linkBeforeAfter.user_uuid === userBeforeAfter.user_uuid);

			return doesLinkMatchLocation && doesLinkMatchUser && linkBeforeAfter.active;
		});
	}

	// Has the selected user been assigned in this location at any time in
	// the past or currently?
	public isSelectedUserHistorical(location: SetupWizardLocation): boolean {
		const locationBeforeAfter = location.after ?? location.before;

		if (!locationBeforeAfter) {
			return false;
		}

		const selectedUser = this.selectedUser();

		if (!selectedUser) {
			return false;
		}

		const userBeforeAfter = selectedUser.after ?? selectedUser.before;

		if (!userBeforeAfter) {
			return false;
		}

		return this.setupWizard.preview$.value.payload.schoolAdminsToLocations.some((userToLocation, index) => {
			const linkBeforeAfter = userToLocation.after ?? userToLocation.before;

			if (!linkBeforeAfter) {
				return false;
			}

			const doesLinkMatchLocation =
				(TextUtils.isNonEmptyString(linkBeforeAfter.sso_location_id) &&
					TextUtils.isNonEmptyString(locationBeforeAfter.sso_location_id) &&
					linkBeforeAfter.sso_location_id === locationBeforeAfter.sso_location_id) ||
				(TextUtils.isNonEmptyString(linkBeforeAfter.location_uuid) &&
					TextUtils.isNonEmptyString(locationBeforeAfter.location_uuid) &&
					linkBeforeAfter.location_uuid === locationBeforeAfter.location_uuid);

			if (!doesLinkMatchLocation) {
				return false;
			}

			const doesLinkMatchUser =
				(TextUtils.isNonEmptyString(linkBeforeAfter.school_id_or_email) &&
					TextUtils.isNonEmptyString(userBeforeAfter.school_id_or_email) &&
					linkBeforeAfter.school_id_or_email === userBeforeAfter.school_id_or_email) ||
				(TextUtils.isNonEmptyString(linkBeforeAfter.user_uuid) &&
					TextUtils.isNonEmptyString(userBeforeAfter.user_uuid) &&
					linkBeforeAfter.user_uuid === userBeforeAfter.user_uuid);

			return doesLinkMatchLocation && doesLinkMatchUser;
		});
	}

	private updateRows(payload: SetupWizardPayload) {
		this.rows.set(payload.payload.locations);
		this.updateFilterOptions();
	}

	private updateFilterOptions(): void {
		const rows = this.rows();

		this.ssoLocationIdFilterOptions = [...new Set(rows.map(row => row.before?.sso_location_id || ''))]
			.sort()
			.map(value => ({
				text: value,
				value: value,
			}));

		this.nameFilterOptions = [...new Set(rows.map(row => row.before?.name || ''))].sort().map(value => ({
			text: value,
			value: value,
		}));

		this.displayOrderFilterOptions = [...new Set(rows.map(row => row.before?.display_order || 0))]
			.sort()
			.map(value => ({
				text: value.toString(),
				value: value,
			}));

		if (this.ssoLocationIdFilterOptions.length > MAX_FILTER_OPTIONS_ALLOWED) {
			this.ssoLocationIdColumn!.filterFn = undefined;
			this.ssoLocationIdColumn!.filterOptions = undefined;
		} else {
			this.ssoLocationIdColumn!.filterOptions = this.ssoLocationIdFilterOptions;
			this.ssoLocationIdColumn!.filterFn = (list: string[], item: SetupWizardLocation) =>
				list.some(name => item.before?.sso_location_id?.indexOf(name) !== -1);
		}

		if (this.nameFilterOptions.length > MAX_FILTER_OPTIONS_ALLOWED) {
			this.nameColumn!.filterFn = undefined;
			this.nameColumn!.filterOptions = undefined;
		} else {
			this.nameColumn!.filterOptions = this.nameFilterOptions;
			this.nameColumn!.filterFn = (list: string[], item: SetupWizardLocation) =>
				list.some(name => item.before?.name?.indexOf(name) !== -1);
		}

		if (this.displayOrderFilterOptions.length > MAX_FILTER_OPTIONS_ALLOWED) {
			this.displayOrderColumn!.filterFn = undefined;
			this.displayOrderColumn!.filterOptions = undefined;
		} else {
			this.displayOrderColumn!.filterOptions = this.displayOrderFilterOptions;
			this.displayOrderColumn!.filterFn = (list: number[], item: SetupWizardLocation) =>
				list.some(name => item.before?.display_order === name);
		}
	}

	public async initTable(): Promise<void> {
		try {
			this.initColumns();

			this.resizeSub?.unsubscribe();
			this.resizeSub = this.browserService.onResize$.subscribe((width: number) => {
				if (width >= FULL_WIDTH_BREAKPOINT) {
					this.columns.set(this.wideColumns);
				} else {
					this.columns.set(this.narrowColumns);
				}
			});
		} catch (error) {
			console.error('Error loading data:', error);
		}
	}

	private initColumns(): void {
		Object.keys(EMPTY_LOCATION_CSV_TEMPLATE).map(key => {
			switch (key) {
				case LocationCsvKeys.ssoLocationId:
					this.ssoLocationIdColumn = {
						key,
						name: LOCATION_CSV_LABELS[LocationCsvKeys.ssoLocationId],
						sortOrder: null,
						sortFn: (a: SetupWizardLocation, b: SetupWizardLocation) =>
							a.before?.sso_location_id!.localeCompare(b.before?.sso_location_id!) ?? -1,
						sortDirections: ['ascend', 'descend', null],
						filterMultiple: true,
					};
					break;
				case LocationCsvKeys.name:
					this.nameColumn = {
						key,
						name: LOCATION_CSV_LABELS[LocationCsvKeys.name],
						sortOrder: null,
						sortFn: (a: SetupWizardLocation, b: SetupWizardLocation) =>
							a.before?.name!.localeCompare(b.before?.name!) ?? -1,
						sortDirections: ['ascend', 'descend', null],
						filterMultiple: true,
					};
					break;

				case LocationCsvKeys.displayOrder:
					this.displayOrderColumn = {
						key,
						name: LOCATION_CSV_LABELS[LocationCsvKeys.displayOrder],
						sortOrder: null,
						sortFn: (a: SetupWizardLocation, b: SetupWizardLocation) =>
							(a.before?.display_order ?? -1) - (b.before?.display_order ?? -1),
						sortDirections: ['ascend', 'descend', null],
						filterMultiple: true,
					};
					break;
				case LocationCsvKeys.active:
					this.activeColumn = {
						key,
						name: LOCATION_CSV_LABELS[LocationCsvKeys.active],
						sortOrder: null,
						// For sorting prefer the after.active property if it exists (is true or false)
						// and fall back to before.active if it is not present
						sortFn: (a: SetupWizardLocation, b: SetupWizardLocation) =>
							(a.after?.active ?? a.before?.active)
								?.toString()
								.localeCompare((b.after?.active ?? b.before?.active)?.toString()!) ?? -1,
						sortDirections: ['ascend', 'descend', null],
						filterMultiple: true,
						filterOptions: [...new Set([SetupWizardBooleanType.TRUE, SetupWizardBooleanType.FALSE])].map(
							value => ({
								text: value,
								value: value,
							})
						),
						filterFn: (list: string[], item: SetupWizardLocation) =>
							list.some(
								name => (item.after?.active ?? item.before?.active)?.toString()?.indexOf(name) !== -1
							),
					};
					break;
				default:
					break;
			}
		});

		if (!this.ssoLocationIdColumn || !this.nameColumn || !this.displayOrderColumn || !this.activeColumn) {
			throw new Error('One or more columns are not defined');
		}

		const statusColumn: Column = makeStatusColumn<SetupWizardLocation>();

		const locationsColumn: Column = makeUsersColumn<SetupWizardLocation>();

		this.wideColumns = [
			this.ssoLocationIdColumn,
			this.nameColumn,
			this.displayOrderColumn,
			this.activeColumn,
			locationsColumn,
			statusColumn,
		];

		// If the caller wants the assigned column
		if (this.columnsFilter().length > 0 && this.columnsFilter().includes(makeAssignedColumn().key)) {
			this.wideColumns.push(makeAssignedColumn(this));
		}

		// If the caller wants the historical column
		if (this.columnsFilter().length > 0 && this.columnsFilter().includes(makeHistoricalColumn().key)) {
			this.wideColumns.push(makeHistoricalColumn(this));
		}

		this.wideColumns = this.wideColumns.filter(column =>
			this.columnsFilter().length > 0 ? this.columnsFilter().includes(column.key) : true
		);

		this.narrowColumns = [this.ssoLocationIdColumn, this.nameColumn, this.activeColumn].filter(column =>
			this.columnsFilter().length > 0 ? this.columnsFilter().includes(column.key) : true
		);

		this.columns.set(this.wideColumns);
	}

	onEditClick(row: SetupWizardLocation) {
		this.onEdit.emit(row);
	}

	onEditUsersClick(row: SetupWizardLocation) {
		this.onEditUsers.emit(row);
	}

	onToggleDisableClick(row: SetupWizardLocation) {
		this.onToggleDisable.emit(row);
	}

	onRemoveClick(row: SetupWizardLocation) {
		this.onRemove.emit(row);
	}

	onResetClick(row: SetupWizardLocation) {
		this.onReset.emit(row);
	}

	onAssignClick(row: SetupWizardLocation) {
		const user = this.selectedUser();
		if (!user) {
			throw new Error('No user selected for assignment');
		}

		this.onAssign.emit({ user, location: row });
	}

	onUnassignClick(row: SetupWizardLocation) {
		const user = this.selectedUser();
		if (!user) {
			throw new Error('No user selected for assignment');
		}

		this.onUnassign.emit({ user, location: row });
	}

	ngOnDestroy() {
		this.sub?.unsubscribe();
		this.resizeSub?.unsubscribe();
	}
}
